import mixpanel from "mixpanel-browser";
import {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { useFormContext } from "react-hook-form";

import { usePreferences } from "./use-preferences";

const findAndReplaceContext = createContext();

// Selections longer than this won't be used as find-and-replace patterns.
// Building a RegExp from thousands of words (e.g. select-all on a large note)
// causes a syntax error or catastrophic backtracking that crashes the page.
// 200 characters covers ~40 words, well beyond any practical search phrase.
const MAX_FIND_REPLACE_LENGTH = 200;

export function useFindAndReplaceContext() {
  return useContext(findAndReplaceContext);
}

function getMatchCount({
  escapedSelectedText,
  replacePartialWords,
  fieldText,
}) {
  const regex = new RegExp(
    replacePartialWords
      ? escapedSelectedText
      : `\\b${escapedSelectedText}\\b`,
    "gi",
  );

  let count = 0;
  Object.keys(fieldText).forEach((key) => {
    if (fieldText[key] && typeof fieldText[key] === "string") {
      const matches = fieldText[key].match(regex);
      const nextCount = matches ? matches.length : 0;
      count = count + nextCount;
    }
  });

  return count;
}

function useProvideFindAndReplace() {
  const [findAndReplaceSelectedText, setFindAndReplaceSelectedText] =
    useState("");
  const [findAndReplaceMatchCount, setFindAndReplaceMatchCount] =
    useState("");
  const [replacementText, setReplacementText] = useState("");
  const [
    shouldShowReplacementTextBox,
    setShouldShowReplacementTextBox,
  ] = useState(false);

  const [replacePartialWords, setReplacePartialWords] =
    useState(false);

  return {
    findAndReplaceSelectedText,
    setFindAndReplaceSelectedText,
    findAndReplaceMatchCount,
    setFindAndReplaceMatchCount,
    replacementText,
    setReplacementText,
    shouldShowReplacementTextBox,
    setShouldShowReplacementTextBox,
    replacePartialWords,
    setReplacePartialWords,
  };
}

export function FindAndReplaceProvider({ children }) {
  const autosave = useProvideFindAndReplace();

  return (
    <findAndReplaceContext.Provider value={autosave}>
      {children}
    </findAndReplaceContext.Provider>
  );
}

export function useFindAndReplace() {
  // This hook requires both FindAndReplaceProvider and FormProvider contexts.
  // Both are always present when this hook is used (see EditableNoteFormWrapper
  // and EditableTemplate where FindAndReplaceProvider is nested inside FormProvider).
  const {
    findAndReplaceSelectedText,
    setFindAndReplaceSelectedText,
    findAndReplaceMatchCount,
    setFindAndReplaceMatchCount,
    replacementText,
    setReplacementText,
    shouldShowReplacementTextBox,
    setShouldShowReplacementTextBox,
    replacePartialWords,
    setReplacePartialWords,
  } = useFindAndReplaceContext();
  const { getValues, reset } = useFormContext();
  const { isFindAndReplaceEnabled } = usePreferences();

  useEffect(() => {
    if (isFindAndReplaceEnabled) {
      setShouldShowReplacementTextBox(
        findAndReplaceSelectedText.length > 0 ? true : false,
      );
    }
  }, [
    findAndReplaceSelectedText,
    isFindAndReplaceEnabled,
    setShouldShowReplacementTextBox,
  ]);

  useEffect(() => {
    const fieldText = getValues();
    setFindAndReplaceMatchCount(
      getMatchCount({
        escapedSelectedText: findAndReplaceSelectedText,
        replacePartialWords,
        fieldText,
      }),
    );
  }, [
    replacePartialWords,
    findAndReplaceSelectedText,
    getValues,
    setFindAndReplaceMatchCount,
  ]);

  useEffect(() => {
    if (!isFindAndReplaceEnabled) {
      setShouldShowReplacementTextBox(false);
    }
  }, [isFindAndReplaceEnabled, setShouldShowReplacementTextBox]);

  function selectFindAndReplaceText(e) {
    let selectedText;
    if (e.target.selectionStart !== undefined) {
      // Native textarea path (legacy)
      const textarea = e.target;
      selectedText = textarea.value.substring(
        textarea.selectionStart,
        textarea.selectionEnd,
      );
    } else {
      // ContentEditable path (TipTap/DSRichTextarea)
      const selection = window.getSelection();
      selectedText = selection?.toString() || "";
    }

    if (selectedText.length > MAX_FIND_REPLACE_LENGTH) {
      clearFindAndReplace();
      return;
    }

    /**
    setSelected text in context, but strip all non-alphanumeric characters
    at the boundaries. It allows alphanumeric characters to be selected
    and replaced, but allows for groups of words to be captured as well with spaces in between.
    Ie:
    Allowed:
    -  "Normal on exam"
    - "Owner"
    Not allowed:
    - "Normal on exam." -> note the trailing period won't be selected
    - "Owner " -> note the trailing space won't be selected
    */
    const selectedTextAlphaNumeric = selectedText.replace(
      /^\W+|\W+$/g,
      "",
    );

    // Escape special characters in the selected text
    const escapeRegExp = (string) => {
      return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    };
    const escapedSelectedText = escapeRegExp(
      selectedTextAlphaNumeric,
    );

    setFindAndReplaceSelectedText(escapedSelectedText);
    const fieldText = getValues();
    setFindAndReplaceMatchCount(
      getMatchCount({
        escapedSelectedText,
        replacePartialWords,
        fieldText,
      }),
    );
  }

  /**
   * Note, this won't apply to objectiveFieldArray (not use-blank objective) or
   * customObjectiveFieldArray due to the nature of the way those form states are nested in react hook from-orange-50
   *
   * Ie, instead of replacing the value at the key of "subjective"
   * we'd need to nest down into the customObjectiveField[x].value for each value.
   *
   * This could *easily* be expanded, but out of scope for release one.
   * Instead, we can just document to users when select/replace is on
   * that this setting does not apply to subfields.
   */
  const handleReplace = () => {
    const fieldText = getValues();
    const regex = new RegExp(
      replacePartialWords
        ? findAndReplaceSelectedText
        : `\\b${findAndReplaceSelectedText}\\b`,
      "gi",
    ); // This ensures only whole words are replaced

    mixpanel.track("Find and Replace", {
      find: findAndReplaceSelectedText,
      replace: replacementText,
    });
    const nextObj = {};

    Object.keys(fieldText).forEach((key) => {
      if (fieldText[key] && typeof fieldText[key] === "string") {
        const replacedText = fieldText[key].replace(
          regex,
          replacementText,
        );

        nextObj[key] = replacedText;
      }
    });

    setFindAndReplaceSelectedText("");
    setFindAndReplaceMatchCount(0);
    setReplacementText("");

    reset({ ...getValues(), ...nextObj });
  };

  function clearFindAndReplace() {
    setFindAndReplaceSelectedText("");
    setFindAndReplaceMatchCount(0);
    setReplacementText("");
    setShouldShowReplacementTextBox(false);
  }

  return {
    selectFindAndReplaceText,
    handleReplace,
    findAndReplaceSelectedText,
    findAndReplaceMatchCount,
    replacementText,
    setReplacementText,
    shouldShowReplacementTextBox,
    clearFindAndReplace,
    replacePartialWords,
    setReplacePartialWords,
  };
}
